// src/services/pushDeviceService.ts — Enregistrement tokens FCM web

import {
  doc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { getToken, deleteToken } from 'firebase/messaging';
import { db, getFirebaseMessaging, getFirebaseVapidKey } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import {
  PUSH_DEVICE_ID_STORAGE_KEY,
  PUSH_DEVICES_SUBCOLLECTION,
} from '../types/pushDevices';

function getOrCreateDeviceId(): string {
  try {
    const existing = window.localStorage.getItem(PUSH_DEVICE_ID_STORAGE_KEY);
    if (existing) return existing;
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `web_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem(PUSH_DEVICE_ID_STORAGE_KEY, id);
    return id;
  } catch {
    return `web_${Date.now()}`;
  }
}

export async function getNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
}

/**
 * Demande la permission OS puis enregistre le token FCM dans
 * `users/{uid}/pushDevices/{deviceId}`.
 */
export async function registerWebPushDevice(params: {
  uid: string;
  businessIds?: string[];
  serviceWorkerRegistration?: ServiceWorkerRegistration | null;
}): Promise<{ ok: boolean; reason?: string; token?: string }> {
  const vapidKey = getFirebaseVapidKey();
  if (!vapidKey) {
    return { ok: false, reason: 'missing_vapid' };
  }

  if (typeof window === 'undefined' || !('Notification' in window)) {
    return { ok: false, reason: 'unsupported' };
  }

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    return { ok: false, reason: permission === 'denied' ? 'denied' : 'permission' };
  }

  const messaging = await getFirebaseMessaging();
  if (!messaging) {
    return { ok: false, reason: 'messaging_unsupported' };
  }

  const registration =
    params.serviceWorkerRegistration ??
    (await navigator.serviceWorker?.ready.catch(() => null));

  let token: string;
  try {
    token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration ?? undefined,
    });
  } catch (err) {
    console.warn('[push] getToken failed', err);
    return { ok: false, reason: 'token_error' };
  }

  if (!token) {
    return { ok: false, reason: 'empty_token' };
  }

  const deviceId = getOrCreateDeviceId();
  const ref = doc(
    db,
    COLLECTIONS.users,
    params.uid,
    PUSH_DEVICES_SUBCOLLECTION,
    deviceId
  );

  await setDoc(
    ref,
    {
      token,
      platform: 'web',
      enabled: true,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : undefined,
      lastSeenAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      ...(params.businessIds?.length ? { businessIds: params.businessIds } : {}),
    },
    { merge: true }
  );

  return { ok: true, token };
}

/** Désactive le device courant (logout / opt-out) sans supprimer l’historique. */
export async function disableCurrentPushDevice(uid: string): Promise<void> {
  try {
    const deviceId = window.localStorage.getItem(PUSH_DEVICE_ID_STORAGE_KEY);
    if (!deviceId) return;
    const ref = doc(db, COLLECTIONS.users, uid, PUSH_DEVICES_SUBCOLLECTION, deviceId);
    await setDoc(
      ref,
      { enabled: false, updatedAt: serverTimestamp(), lastSeenAt: serverTimestamp() },
      { merge: true }
    );
  } catch (err) {
    console.warn('[push] disableCurrentPushDevice failed', err);
  }
}

export async function refreshPushTokenLastSeen(params: {
  uid: string;
  businessIds?: string[];
}): Promise<void> {
  const permission = await getNotificationPermission();
  if (permission !== 'granted') return;
  const vapidKey = getFirebaseVapidKey();
  if (!vapidKey) return;

  const messaging = await getFirebaseMessaging();
  if (!messaging) return;

  try {
    const registration = await navigator.serviceWorker?.ready.catch(() => null);
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration ?? undefined,
    });
    if (!token) return;

    const deviceId = getOrCreateDeviceId();
    await setDoc(
      doc(db, COLLECTIONS.users, params.uid, PUSH_DEVICES_SUBCOLLECTION, deviceId),
      {
        token,
        platform: 'web',
        enabled: true,
        lastSeenAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...(params.businessIds?.length ? { businessIds: params.businessIds } : {}),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn('[push] refreshPushTokenLastSeen failed', err);
  }
}

/** Best-effort : invalide le token FCM local (ne bloque pas le logout). */
export async function deleteLocalFcmToken(): Promise<void> {
  try {
    const messaging = await getFirebaseMessaging();
    if (!messaging) return;
    await deleteToken(messaging);
  } catch {
    // ignore
  }
}
